<template>
    <vue-popper
        class="lg:hidden"
        placement="bottom-end"
        :offset-distance="8"
        :arrow="false"
        :interactive="true"
        @open:popper="handleMenuOpen"
        @close:popper="handleMenuClose"
    >
        <button
            ref="menuTrigger"
            type="button"
            class="flex items-center justify-center leading-[17px] -mr-[6px]"
            aria-label="Menu"
            @keydown.capture="handleKeyboardIntent"
        >
            <span class="underline">{{ $t('OPTIONS') }}</span>
            <img
                v-if="showOptionsMenu"
                src="../assets/images/icons/close.svg"
                alt="Close"
                class="w-4 h-4 ml-1"
            />
            <img
                v-else
                src="../assets/images/icons/stacked-dots.svg"
                alt="Open"
                class="w-4 h-4 ml-1"
            />
        </button>

        <template #content="{ close }">
            <focus-lock :disabled="!showOptionsMenu" group="side-panel">
                <div
                    ref="menuContent"
                    class="w-[190px] py-1 bg-white rounded-lg shadow-lg border border-gray-DFDFDF"
                    @keydown.capture="handleMenuKeydown"
                >
                    <button
                        ref="firstMenuItem"
                        type="button"
                        :class="[
                            'w-full text-left px-3 py-2 hover:bg-gray-F6F6F6 focus:bg-gray-F6F6F6',
                            'focus:outline-none focus:ring-1 focus:ring-black focus:border-black ring-offset-2',
                            'flex items-center gap-2'
                        ]"
                        @click="handleMenuAction(close, 'summary')"
                    >
                        <summary-icon class="w-4 h-4" />
                        {{ $t('SUMMARY') }}
                    </button>
                    <button
                        type="button"
                        :class="[
                            'w-full text-left px-3 py-2 hover:bg-gray-F6F6F6 focus:bg-gray-F6F6F6',
                            'focus:outline-none focus:ring-1 focus:ring-black focus:border-black ring-offset-2',
                            'flex items-center gap-2'
                        ]"
                        @click="handleMenuAction(close, 'share')"
                    >
                        <img
                            src="../assets/images/icons/share.svg"
                            alt="Share"
                            class="w-4 h-4"
                        >
                        {{ $t('SHARE_DESIGN') }}
                    </button>
                    <button
                        type="button"
                        :class="[
                            'w-full text-left px-3 py-2 hover:bg-gray-F6F6F6 focus:bg-gray-F6F6F6',
                            'focus:outline-none focus:ring-1 focus:ring-black focus:border-black ring-offset-2',
                            'flex items-center gap-2'
                        ]"
                        @click="handleMenuAction(close, 'help')"
                    >
                        <img src="../assets/images/icons/help.svg" alt="Help" class="w-4 h-4" />
                        {{ $t('HELP') }}
                    </button>
                    <div class="pb-1 mx-3 border-t border-gray-DFDFDF" />
                    <button
                        type="button"
                        :class="[
                            'flex items-center gap-2 text-left px-3 py-2 w-full',
                            'hover:bg-gray-F6F6F6 focus:bg-gray-F6F6F6',
                            'focus:outline-none focus:ring-1 focus:ring-black focus:border-black ring-offset-2',
                        ]"
                        @click="handleResetClick(close)"
                    >
                        <img src="../assets/images/icons/reset.svg" alt="Reset" class="w-4 h-4" />
                        <span class="w-full text-left font-normal hover:text-black flex items-center gap-2">
                            {{ $t('RESET_TO_DEFAULT') }}
                        </span>
                    </button>
                </div>
            </focus-lock>
        </template>
    </vue-popper>

    <teleport to="#cz3" v-if="showResetModal">
        <base-modal-confirm
            @modalClose="showResetModal = false"
            @confirmAndExit="handleResetConfirm"
            :show-modal="showResetModal"
            :modal-heading="$t('RESET_CONFIRM')"
            :modal-copy="$t('RESET_CONFIRM_COPY')"
            :cta-copy="$t('RESET_TO_DEFAULT')"
        />
    </teleport>
</template>

<script>
    import FocusLock from 'vue-focus-lock';
    import BaseModalConfirm from './BaseModalConfirm.vue';
    import SummaryIcon from './SummaryIcon.vue';

    export default {
        emits: ['summary', 'share', 'help', 'reset'],

        components: {
            BaseModalConfirm,
            FocusLock,
            SummaryIcon,
        },

        data() {
            return {
                showOptionsMenu: false,
                showResetModal: false,
            };
        },

        methods: {
            handleMenuOpen() {
                this.showOptionsMenu = true;
                this.handleKeyboardIntent();
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        this.$refs.firstMenuItem?.focus();
                    });
                });
            },

            handleMenuClose() {
                this.showOptionsMenu = false;
            },

            handleKeyboardIntent() {
                document.getElementById('cz3')?.classList.remove('cz3--hide-outlines');
            },

            getMenuItems() {
                return Array.from(
                    this.$refs.menuContent?.querySelectorAll('button:not([disabled])') || [],
                );
            },

            handleMenuKeydown(event) {
                this.handleKeyboardIntent();

                if (event.key !== 'Tab') {
                    return;
                }

                const menuItems = this.getMenuItems();

                if (menuItems.length === 0) {
                    return;
                }

                const currentIndex = menuItems.indexOf(document.activeElement);

                if (event.shiftKey && currentIndex === 0) {
                    event.preventDefault();
                    menuItems[menuItems.length - 1]?.focus();
                    return;
                }

                if (!event.shiftKey && currentIndex === menuItems.length - 1) {
                    event.preventDefault();
                    menuItems[0]?.focus();
                }
            },

            async handleMenuAction(close, action) {
                if (typeof close === 'function') {
                    close();
                }

                this.showOptionsMenu = false;

                await new Promise((resolve) => { setTimeout(resolve, 250); });

                this.$emit(action);
            },

            handleResetClick(close) {
                if (typeof close === 'function') {
                    close();
                }

                this.showOptionsMenu = false;
                this.showResetModal = true;
            },

            handleResetConfirm() {
                this.showOptionsMenu = false;
                this.showResetModal = false;
                this.$emit('reset');
            },
        },
    };
</script>
